import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import http from '../utils/http';
import { AffiliateReporting, bulkFactValues, factValue } from './AfrSlice';
import { Concept, ConceptDisplayRef } from './ConceptSlice';
import { Edge } from './EntitiesSlice';
import { Network } from './NetworkSlice';
import { RootState } from './store';
import { TaxonomyTypeEnum } from './TaxonomyVersionSlice';

    
export interface Workflow {
    EXCELSequence: number;  /* possible values: 1, 2, 3 */
    XBRLSequence: number; /* possible values: 1, 2 */
}

export interface Edit {
    IsDirty: boolean;
    Page: string | null;
}

export interface AffiliateAuditor {
    affiliateEntityCode: string,
    affiliateName: string,
    auditor: string
}

export interface AfrOutOfSyncNotification {
    subject: string;
    message: string;
}

export interface ColumnFilterAccountWithoutFact {
    accountCode: string;
    columnFilter: string;
}

export interface MiscState {
    edit: Edit;
    workflowState: Workflow
    IsAdmin: boolean;
    affiliateAuditors: Array<AffiliateAuditor>,
    tableFacts: Array<factValue>,
    affiliates: Array<AffiliateReporting>,
    replaceTableFacts: bulkFactValues | null;
    deprecationSubstitutionSelections: ConceptDisplayRef[];
    taxonomyType: TaxonomyTypeEnum | null;
    dimensionSectionMap: any;
    sectionDimensionMap: any;
    isHome: boolean;
    panelsAndNavigationEnabled: boolean;
    isSaving?: boolean;
    columnFilterAccountsWithoutFacts?: Array<ColumnFilterAccountWithoutFact>;
}

const initialState: MiscState = {
    edit: {IsDirty: false, Page: ""},
    workflowState: { EXCELSequence: 1, XBRLSequence: 1 },
    IsAdmin: true,
    affiliateAuditors: [],
    tableFacts: [],
    affiliates: [],
    replaceTableFacts: null,
    deprecationSubstitutionSelections: [],
    taxonomyType: TaxonomyTypeEnum.AFR,
    dimensionSectionMap: {},
    sectionDimensionMap: {},
    isHome: false,
    panelsAndNavigationEnabled: true,
    isSaving: false,
    columnFilterAccountsWithoutFacts: [],
}

export const getReportNotify = createAsyncThunk(
    "Afr/getReportNotify",
    async (afrVersionDetail: { afrId: string, versionNumber: number, taxononmyType: string }): Promise<AfrOutOfSyncNotification | null> => {
        try {
            const result = await http<string, AfrOutOfSyncNotification>({
                path: `/afr/getReportNotify/${afrVersionDetail.afrId}/${afrVersionDetail.versionNumber}/${afrVersionDetail.taxononmyType}/`,
                method: `get`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

const addToDimensionSectionMap = function (section: string, edges: Edge[] | null, sectMap : any) {
    if (!edges) {
        return;
    }
    sectMap[section] = section;
    edges.map(e => {
        var nd = e.to;
        if (nd.outgoing && nd.outgoing.length > 0) {
            nd.outgoing.map(echild => addToDimensionSectionMap(echild.to.concept.id, echild.to.outgoing, sectMap));
        } else {
            sectMap[nd.concept.id] = section;
        }
    })
}
export const createDimensionSectionMap = function(n: Network) : any {
    var result = {};
    if (n) {
        n.roots.map(n => addToDimensionSectionMap(n.concept.id, n.outgoing, result));
    }
    return result;
}
const addToSectionDimensionMap = function (section: Concept, edges: Edge[] | null, sectMap: any) {
    if (!edges) {
        return;
    }
    edges.map(e => {
        var nd = e.to;
        if (nd.outgoing && nd.outgoing.length > 0) {
            nd.outgoing.map(echild => addToSectionDimensionMap(echild.to.concept, echild.to.outgoing, sectMap));
        } else {
            if (sectMap.hasOwnProperty(section.id)) {
                var dimList: Concept[] = sectMap[section.id];
                dimList.push(nd.concept);
            } else {
                sectMap[section.id] = [section, nd.concept];
            }
        }
    })
}
export const createSectionDimensionMap = function (n: Network): any {
    var result = {};
    if (n) {
        n.roots.map(n => addToSectionDimensionMap(n.concept, n.outgoing, result));
    }
    return result;
}
export const getAuditorFromAffiliate = function (affiliates: Array<AffiliateAuditor> | null, affiliateCode: string): AffiliateAuditor | null {

    if (affiliates !== null) {
        const updIdx = affiliates.findIndex(x => x.affiliateEntityCode === affiliateCode);
        if (updIdx === -1) {
            return null;
        } else {
            return affiliates[updIdx];
        }
    } else {
        return null;
    }
}

/* ------------------------------------------------------------------------------------------------------------------
  This is a hack to call a selector using an arrow function that returns a Thunk.
  In this way we can get the state directly from the slice and we can pass in the parameter the selector
  to get the specific piece of the slice we need.
  That function can be called whenerver we want to call the selector.
  Since dispatching a thunk returns the thunk return value, we could write a thunk that accepts a selector,
  and immediately calls the selector with the state and returns the result.
  This was discovered reading the documentation about Thunks, because it receive a second parameter called getState
  which allow us to reach the slice of the state we need.
  -------------------------------------------------------------------------------------------------------------------*/
export const getSelector = (selector: any) => (dispatch: any, getState: any) => {
    return selector(getState());
}


export const getMiscAffiliate = function (affiliates: Array<AffiliateReporting>, affiliateCode: string) {
    if (affiliates && affiliates.length > 0) {
        var affiliatedEntity = affiliates.find(x => x.affiliateReference && (x.affiliateReference.code === affiliateCode));
        if ((typeof affiliatedEntity) === "undefined") return null;
        return affiliatedEntity;
    }
    return null;
}



export const miscSlice = createSlice({
    name: 'Misc',
    initialState,
    reducers: {        
        setIsDirty: (state: any, action: any) => {
            if (!action.payload.IsDirty) {
                state.edit = initialState.edit
                state.workflowState = { EXCELSequence: 1, XBRLSequence: 1 };
            }
            state.edit = action.payload;
        },
        setTaxonomyType: (state: any, action: any) => {
            state.taxonomyType = action.payload;
        },
        setIsAdmin: (state: any, action: any) => {
            state.IsAdmin = action.payload;
        },
        setWorkflowStatus: (state: any, action: any) => {
            state.workflowState = action.payload;
        },
        resetAffiliateAuditors: (state: any, action: any) => {
            state.affiliateAuditors = [];
        },
        updateAffiliateAuditors: (state: any, action: any) => {
            const affAuditors: Array<AffiliateAuditor> = state.affiliateAuditors;
            const affAuditor: AffiliateAuditor= action.payload;
            const updIdx = affAuditors.findIndex(x => x.affiliateEntityCode === affAuditor.affiliateEntityCode);
            if (updIdx === -1) {
                affAuditors.push(affAuditor);
            } else {
                affAuditors[updIdx].auditor = affAuditor.auditor;
            }
                
        },
        resetTableFacts: (state: any, action: any) => {
            state.tableFacts = [];
        },
        updateTableFacts: (state: any, action: any) => {
        
            var tableName: string;
            const facts: Array<factValue> = state.tableFacts;
            const newFacts: Array<factValue> = action.payload;
            var mergedFacts: Array<factValue> = [];
            console.log("newFacts", newFacts);
            if (newFacts && newFacts.length === 0) {
                state.tableFacts = [];
                return;
            }

            if (newFacts && newFacts.length > 0) {
                tableName = newFacts[0].tableId;
            }

            /* remove the records when TableId = tableName */
            mergedFacts = facts.filter(x => x.tableId != tableName);
            mergedFacts.push(...newFacts);
            state.tableFacts = mergedFacts;
            
        },
        setReplaceTableFacts: (state: any, action: any) => {

            state.replaceTableFacts = action.payload;

        },
        resetReplaceTableFacts: (state: any, action: any) => {

            state.replaceTableFacts = null;

        },
        updateTableCell: (state: any, action: any) => {

            const facts: Array<factValue> = state.tableFacts;
            const updFact: factValue = action.payload;

            /* Verify if this fact exist, if so then update it, If not so then insert */

            var index = facts.findIndex(x => (  (x.tableId === updFact.tableId) &&
                                                (x.rowId === updFact.rowId) &&
                                                (x.fundGroup === updFact.fundGroup) &&
                                                (x.concept === updFact.concept)));

            if (index > -1) {
                facts[index] = updFact;
            } else {
                facts.push(updFact);
            }

        },
        clearTableRow: (state: any, action: any) => {

            const facts: Array<factValue> = state.tableFacts;
            const updFact: factValue = action.payload;

            /* This fact is a placeholder to identify fact entries to be filtered out */

            var filteredFacts: factValue[] = facts.filter(x => ((x.tableId === updFact.tableId) && (x.rowId === updFact.rowId)));
            state.tableFacts = filteredFacts;
        },
        resetAffiliates: (state: any, action: any) => {
            state.affiliates = [];
        },
        updateAffiliate: (state: any, action: any) => {

            if ( !(action.payload) ) {
                return;
            }

            const affiliates: Array<AffiliateReporting> = state.affiliates
            const affiliatedEntity: AffiliateReporting = action.payload;
            var matchCode = affiliatedEntity ? affiliatedEntity.affiliateReference ? affiliatedEntity.affiliateReference.code : "" : "";
            var index = affiliates.findIndex(x => x.affiliateReference ? x.affiliateReference.code === matchCode : false);

            if (index > -1) {
                affiliates[index] = affiliatedEntity;
            } else {
                affiliates.push(affiliatedEntity);
            }

        },
        setDeprecationSubs: (state: any, action: any) => {
            if (!(action.payload)) {
                return;
            }

            state.deprecationSubstitutionSelections = action.payload;
        },
        setDimensionSectionMap: (state: any, action: any) => {
            state.dimensionSectionMap = createDimensionSectionMap(action.payload);
            state.sectionDimensionMap = createSectionDimensionMap(action.payload);
        },
        setIsHome: (state: any, action: any) => {
            state.isHome = action.payload;
        },
        setPanelsAndNavigationEnabled: (state: any, action: any) => {
            state.panelsAndNavigationEnabled = action.payload;
        },
        setIsLoading: (state: any, action: any) => {
            state.isSaving = action.payload;
        },
        addColumnFilterAccountWithoutFacts: (state: any, action: any) => {
            const newEntry: ColumnFilterAccountWithoutFact = action.payload;
            const existing: Array<ColumnFilterAccountWithoutFact> = state.columnFilterAccountsWithoutFacts;
            const alreadyExists = existing.some((x: ColumnFilterAccountWithoutFact) =>
                x.accountCode === newEntry.accountCode && x.columnFilter === newEntry.columnFilter);
            if (!alreadyExists) {
                state.columnFilterAccountsWithoutFacts = [...existing, newEntry];
            }
        },
        removeColumnFilterAccountWithoutFacts: (state: any, action: any) => {
            const entryToRemove: ColumnFilterAccountWithoutFact = action.payload;
            state.columnFilterAccountsWithoutFacts = state.columnFilterAccountsWithoutFacts.filter((x: ColumnFilterAccountWithoutFact) =>
                !(x.accountCode === entryToRemove.accountCode && x.columnFilter === entryToRemove.columnFilter)
            );
        },
        resetColumnFilterAccountsWithoutFacts: (state: any, action: any) => {
            state.columnFilterAccountsWithoutFacts = [];
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getReportNotify.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getReportNotify.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
    },
});

export const { setIsDirty, setIsAdmin, setTaxonomyType, setWorkflowStatus, resetAffiliateAuditors,
    updateAffiliateAuditors, resetTableFacts, updateTableFacts, updateAffiliate, updateTableCell, resetAffiliates,
    setReplaceTableFacts, resetReplaceTableFacts, setDeprecationSubs, clearTableRow, setDimensionSectionMap,
    setIsHome, setPanelsAndNavigationEnabled, setIsLoading, addColumnFilterAccountWithoutFacts, resetColumnFilterAccountsWithoutFacts,
    removeColumnFilterAccountWithoutFacts } = miscSlice.actions;

export const getMisc = (state: RootState) => state.misc ? state.misc : null;

export const isDirty = (state: RootState) => state.misc ? state.misc.edit.IsDirty : false;

export const getWorkflowState = (state: RootState) => state.misc ? state.misc.workflowState : null;

export const getAffiliatesAuditors = (state: RootState) => state.misc ? state.misc.affiliateAuditors : null;

export const getTableFacts = (state: RootState) => state.misc ? state.misc.tableFacts : null;

export const getAffiliates = (state: RootState) => state.misc ? state.misc.affiliates : null;

export const getReplaceTableFacts = (state: RootState) => state.misc ? state.misc.replaceTableFacts : null;

export const getDeprecationSubs = (state: RootState) => state.misc ? state.misc.deprecationSubstitutionSelections : [];

export const isAdmin = (state: RootState) => state.user && state.user.userProfile &&
    state.user.userProfile.role == "ADMIN" ? true : false;

export const getTaxonomyType = (state: RootState) => state.misc ? state.misc.taxonomyType : 'AFR';

export const getDimensionSectionMap = (state: RootState) => state.misc ? state.misc.dimensionSectionMap : {};

export const getSectionDimensionMap = (state: RootState) => state.misc ? state.misc.sectionDimensionMap : {};

export const getIsHome = (state: RootState) => state.misc ? state.misc.isHome : false;

export const panelsAndNavigationEnabledSelect = (state: RootState) => state.misc ? state.misc.panelsAndNavigationEnabled : true;

export const isSavingSelect = (state: RootState) => state.misc ? state.misc.isSaving : false;

export const selectColumnFilterAccountsWithoutFacts = (state: RootState) => state.misc ? state.misc.columnFilterAccountsWithoutFacts : [];

export default miscSlice.reducer;